import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { fullNameUsernameOrEmail } from '../../../../../helpers/username';
import { trackAction } from '../../../../../helpers/trackAction';
import { render, createPortal } from 'react-dom';
import CloudinaryUploadWidget from '../../../components/CloudinaryUploadWidget';
import Loader from '../../../../../components/Loader';

interface UseFileUploadBlockProps {
  content: { pk: string };
  studyGroup: { pk: string };
  currentSectionCounter: number;
  iFrame: any;
  isContentLoading: boolean;
}

const getFileUploads = (iFrameRef: any, currentSectionCounter: number) => {
  const iframeDoc = iFrameRef.current.contentWindow.document;
  const quizQuestions = iframeDoc.querySelectorAll(
    `.content_section:nth-of-type(${currentSectionCounter + 1}) .file-block`,
  );
  return quizQuestions as HTMLElement[];
};

const getCurrentFileSlug = (
  iFrameRef: any,
  currentSectionCounter: number,
  questionIndex: number,
) => {
  const fileUploads = getFileUploads(iFrameRef, currentSectionCounter);
  if (!fileUploads[questionIndex]) {
    return -1;
  }
  return (fileUploads[questionIndex] as any).attributes[
    'data-file-block-slug'
  ]!.value.replace('data-file-block-slug=', '').replace('"', '');
};

const getFileUploadType = (
  iFrameRef: any,
  currentSectionCounter: number,
  questionIndex: number,
) => {
  const fileUploads = getFileUploads(iFrameRef, currentSectionCounter);
  if (!fileUploads[questionIndex]) {
    return -1;
  }
  return JSON.parse(
    (fileUploads[questionIndex] as any).querySelector('#type').textContent,
  );
};

const getStudents = (
  studyGroupId: string,
  setStudents: (students: any) => void,
) => {
  axios
    .get('/api/v1/users/students/', {
      params: {
        format: 'json',
        study_group: studyGroupId,
      },
      headers: {
        'X-CSRFToken': JSON.parse(
          document.getElementById('csrf_token')!.innerHTML,
        ),
      },
    })
    .then((response) => {
      setStudents(response.data.results);
    })
    .catch((e) => console.error(e));
};

const useFileUploadBlock = ({
  content,
  studyGroup,
  currentSectionCounter,
  iFrame,
  isContentLoading,
}: UseFileUploadBlockProps) => {
  const user = useContext(AuthContext);
  const [areStudentsLoaded, setAreStudentsLoaded] = useState(false);
  const [students, setStudents] = useState<any[]>([]);
  const [studentAnswers, setStudentAnswers] = useState<Record<number, any>>({});
  const [doneSections, setDoneSections] = useState<number[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [isSlugLoading, setIsSlugLoading] = useState<Record<string, boolean>>(
    {},
  );

  const isTeacher = user?.roles.includes(Role.teacher);

  useEffect(() => {
    if (isContentLoading) {
      return;
    }
    if (isTeacher && !areStudentsLoaded) {
      return;
    }
    if (doneSections.includes(currentSectionCounter)) {
      return;
    }
    setDoneSections((d) => [...d, currentSectionCounter]);
    setTimeout(() => {
      if (!iFrame.current) {
        return;
      }
      fileUploadInit();
    }, 1000);
  }, [isContentLoading, currentSectionCounter, areStudentsLoaded]);

  useEffect(() => {
    if (isTeacher && studyGroup) {
      getStudents(studyGroup.pk, (s: any) => {
        setStudents(s);
        setAreStudentsLoaded(true);
      });
    }
  }, [studyGroup]);

  const saveFile = (slug: string, url: string, filename: string) => {
    isSlugLoading[slug] = true;
    setIsSlugLoading({ ...isSlugLoading });
    axios
      .post(
        '/api/v1/syllabus/user_file_upload/',
        {
          slug,
          class_id: studyGroup.pk,
          file_url: url,
          filename,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      )
      .then((response) => {})
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        isSlugLoading[slug] = false;
        setIsSlugLoading({ ...isSlugLoading });
      });
  };

  const fileUploadInit = () => {
    const quizQuestions = getFileUploads(iFrame, currentSectionCounter);
    for (let i = 0; i < quizQuestions.length; i++) {
      getOldFiles(i);
    }
  };

  const getOldFiles = (questionIndex: number) => {
    const quizQuestions = getFileUploads(iFrame, currentSectionCounter);
    if (!quizQuestions[questionIndex]) {
      return;
    }
    const currentFileSlug = getCurrentFileSlug(
      iFrame,
      currentSectionCounter,
      questionIndex,
    );

    const fileUploadType = getFileUploadType(
      iFrame,
      currentSectionCounter,
      questionIndex,
    );

    const deleteFile = () => {
      axios
        .delete('/api/v1/syllabus/user_file_upload/', {
          params: {
            class_id: studyGroup.pk,
            slug: currentFileSlug,
          },
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        })
        .then((response) => {
          // eslint-disable-next-line no-restricted-globals
          location.reload();
        });
    };

    axios
      .get('/api/v1/syllabus/user_file_upload/', {
        params: {
          class_id: studyGroup.pk,
          slug: currentFileSlug,
        },
        headers: {
          'X-CSRFToken': JSON.parse(
            document.getElementById('csrf_token')!.innerHTML,
          ),
        },
      })
      .then((response) => {
        const container = quizQuestions[questionIndex]!.querySelector(
          '.file-container',
        ) as HTMLElement;
        let initialUrl = null;
        let initialFilename = null;
        let data: any = [];
        try {
          data = JSON.parse(response.data);
          if (data.length) {
            initialUrl = data[0].fields.file_url;
            initialFilename = data[0].fields.filename;
          }
        } catch (e) {}

        if (isTeacher) {
          render(
            <div>
              {students.map((s: any, index: number) => {
                const d = data.find(
                  ({ fields }: any) => fields.student_id === s.pk,
                );
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      margin: '4px',
                    }}
                  >
                    <span style={{ width: '260px', maxWidth: '260px' }}>
                      {fullNameUsernameOrEmail(s)}
                    </span>
                    <CloudinaryUploadWidget
                      isViewOnly={true}
                      isImageOnly={fileUploadType === 'image'}
                      initialUrl={d?.fields?.file_url || null}
                      initialFilename={d?.fields?.filename || null}
                      onImageUpload={({
                        url,
                        original_filename,
                        resource_type,
                      }) => {
                        urls[currentFileSlug] = url;
                        setUrls({ ...urls });
                        saveFile(currentFileSlug, url, original_filename);
                      }}
                    />
                  </div>
                );
              })}
            </div>,
            container,
          );
        } else {
          render(
            <div>
              <CloudinaryUploadWidget
                isImageOnly={fileUploadType === 'image'}
                initialUrl={initialUrl}
                initialFilename={initialFilename}
                onImageUpload={({ url, original_filename, resource_type }) => {
                  urls[currentFileSlug] = url;
                  setUrls({ ...urls });
                  saveFile(currentFileSlug, url, original_filename);
                }}
                onDelete={() => {
                  deleteFile();
                }}
              />
            </div>,
            container,
          );
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };
};

export default useFileUploadBlock;
